const NEARBY_QUERY_PATTERNS = [
  /\bnear me\b/i,
  /\bnearby\b/i,
  /\baround me\b/i,
  /\bclose to\b/i,
  /\bclose by\b/i,
];

const isUsableText = (value) => {
  const text = `${value ?? ""}`.trim();
  return text.length > 0 && text.toLowerCase() !== "unknown";
};

const queryMentionsCity = (query, city) => {
  if (!isUsableText(query) || !isUsableText(city)) return false;

  return `${query}`.toLowerCase().includes(`${city}`.trim().toLowerCase());
};

const getQueryWithSelectedCity = (query, selectedCity) => {
  const trimmedQuery = `${query ?? ""}`.trim();

  if (!trimmedQuery || !isUsableText(selectedCity)) {
    return trimmedQuery;
  }

  if (queryMentionsCity(trimmedQuery, selectedCity)) {
    return trimmedQuery;
  }

  return `${trimmedQuery} in ${selectedCity}`;
};

export const isNearbySearchQuery = (query) => {
  if (!query) return false;

  return NEARBY_QUERY_PATTERNS.some((pattern) => pattern.test(query));
};

export const getLiveLocationPayload = ({ locationData, coords, trainStation } = {}) => {
  const directCoordinates = Array.isArray(locationData?.coordinates)
    ? locationData.coordinates
    : null;

  const fallbackCoordinates =
    Number.isFinite(coords?.longitude) && Number.isFinite(coords?.latitude)
      ? [coords.longitude, coords.latitude]
      : null;

  const coordinates = directCoordinates || fallbackCoordinates;

  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    return null;
  }

  const [longitude, latitude] = coordinates;
  if (!Number.isFinite(Number(longitude)) || !Number.isFinite(Number(latitude))) {
    return null;
  }

  const payload = {
    coordinates: [Number(longitude), Number(latitude)],
  };

  // If train station is available, use it; otherwise use location data
  if (trainStation) {
    payload.locality = trainStation.locality;
    payload.city = trainStation.city;
    payload.trainStation = trainStation.name;
  } else {
    if (isUsableText(locationData?.city)) {
      payload.city = `${locationData.city}`.trim();
    }

    if (isUsableText(locationData?.locality)) {
      payload.locality = `${locationData.locality}`.trim();
    }
  }

  return payload;
};

const resolveSearchCity = ({
  locationData,
  selectedCity,
  userBaseCity,
  preferSelectedCity = false,
} = {}) => {
  if (preferSelectedCity && isUsableText(selectedCity)) {
    return `${selectedCity}`.trim();
  }

  if (isUsableText(locationData?.city)) {
    return `${locationData.city}`.trim();
  }

  if (isUsableText(selectedCity)) {
    return `${selectedCity}`.trim();
  }

  if (isUsableText(userBaseCity)) {
    return `${userBaseCity}`.trim();
  }

  return null;
};

export const buildAiSearchParams = ({
  query,
  selectedCity,
  userBaseCity,
  locationData,
  coords,
  trainStation,
  ignoreLiveLocation = false,
  preferSelectedCity = false,
  appendCityToQuery,
  includeCity = true,
}) => {
  const shouldAppendCityToQuery =
    includeCity && (appendCityToQuery ?? preferSelectedCity);
  const trimmedQuery =
    shouldAppendCityToQuery && isUsableText(selectedCity)
      ? getQueryWithSelectedCity(query, selectedCity)
      : `${query ?? ""}`.trim();
  const isNearbyQuery = isNearbySearchQuery(trimmedQuery);
  const liveLocation = ignoreLiveLocation
    ? null
    : getLiveLocationPayload({ locationData, coords, trainStation });
  const locationTrainStation = ignoreLiveLocation ? null : trainStation;

  const params = new URLSearchParams();
  params.append("q", trimmedQuery);

  if (
    includeCity &&
    ignoreLiveLocation &&
    preferSelectedCity &&
    isUsableText(selectedCity)
  ) {
    params.append(
      "location",
      JSON.stringify({
        city: `${selectedCity}`.trim(),
      })
    );
  }

  if (liveLocation) {
    params.append("location", JSON.stringify(liveLocation));
    
    // Use train station data if available
    if (locationTrainStation) {
      params.append("city", locationTrainStation.city);
      params.append("locality", locationTrainStation.locality);
      params.append("trainStation", locationTrainStation.name);
    } else {
      const searchCity = resolveSearchCity({
        locationData,
        selectedCity,
        userBaseCity,
        preferSelectedCity,
      });

      if (searchCity) params.append("city", searchCity);
      if (isUsableText(locationData?.locality)) {
        params.append("locality", `${locationData.locality}`.trim());
      }
    }

    if (userBaseCity) params.append("userBaseCity", userBaseCity);

    return {
      params,
      isNearbyQuery,
      usedLiveLocation: true,
      liveLocation,
    };
  }

  if (isNearbyQuery && !liveLocation) {
    if (userBaseCity) params.append("userBaseCity", userBaseCity);
    return {
      params,
      isNearbyQuery,
      usedLiveLocation: false,
      liveLocation: null,
      usedFallbackCity: false,
      needsLiveLocation: true,
    };
  }

  if (!includeCity) {
    return {
      params,
      isNearbyQuery,
      usedLiveLocation: false,
      liveLocation,
    };
  }

  const city = resolveSearchCity({
    locationData,
    selectedCity,
    userBaseCity,
    preferSelectedCity,
  });

  if (city) params.append("city", city);
  if (userBaseCity) params.append("userBaseCity", userBaseCity);

  return {
    params,
    isNearbyQuery,
    usedLiveLocation: false,
    liveLocation,
  };
};
